import React, { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { ClipboardList, Download } from 'lucide-react';
import { PublicDataProvider } from '../../../context/PublicDataContext';
import { InquiryCartProvider, useInquiryCart } from '../../../context/InquiryCartContext';
import { WishlistProvider } from '../../../context/WishlistContext';
import PublicHeader from './PublicHeader';
import PublicFooter from './PublicFooter';
import InquiryCartDrawer from './InquiryCartDrawer';
import SearchOverlay from './SearchOverlay';
// import PublicAssistant from './PublicAssistant';   // Ask Moji — hidden for now

/* Injects the brand webfonts + global public-site styles exactly once. Kept out
 * of index.html so this whole feature stays self-contained and droppable. */
function GlobalStyles() {
  useEffect(() => {
    if (document.getElementById('moji-public-fonts')) return;
    const pre1 = document.createElement('link');
    pre1.rel = 'preconnect';
    pre1.href = 'https://fonts.googleapis.com';
    const pre2 = document.createElement('link');
    pre2.rel = 'preconnect';
    pre2.href = 'https://fonts.gstatic.com';
    pre2.crossOrigin = 'anonymous';
    const font = document.createElement('link');
    font.id = 'moji-public-fonts';
    font.rel = 'stylesheet';
    font.href =
      'https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,500;9..144,600;9..144,700&family=Inter:wght@300;400;500;600&display=swap';
    document.head.append(pre1, pre2, font);
  }, []);

  return (
    <style>{`
      .font-display { font-family: 'Fraunces', Georgia, serif; }
      .reveal { opacity: 0; transform: translateY(18px); transition: opacity .7s ease, transform .7s cubic-bezier(.22,.61,.36,1); }
      .reveal-in { opacity: 1; transform: none; }
      .line-clamp-2 { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
      @media (prefers-reduced-motion: reduce) {
        .reveal { transition: none; opacity: 1; transform: none; }
        html { scroll-behavior: auto; }
      }
      html { scroll-behavior: smooth; }
    `}</style>
  );
}

/* Mobile-only "Install App" prompt — bottom-left so it never collides with
 * the sticky inquiry button (bottom-right). Only renders when the browser
 * actually deems the site installable. */
function InstallAppButton() {
  const [installPrompt, setInstallPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    const onBeforeInstall = (e) => { e.preventDefault(); setInstallPrompt(e); };
    const onInstalled = () => { setInstalled(true); setInstallPrompt(null); };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  // On the website (moji.org.in) the button opens the software, which is the app to install
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  const standalone = typeof window !== 'undefined' && (window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true);
  if (host === 'moji.org.in' || host === 'www.moji.org.in') {
    if (standalone) return null;
    return (
      <a
        href="https://app.moji.org.in/login?install=1"
        className="md:hidden fixed bottom-5 left-5 z-[55] flex items-center gap-2 bg-[#b5622f] text-white pl-4 pr-5 py-3.5 rounded-full shadow-2xl text-[12px] uppercase tracking-[0.12em]"
      >
        <Download className="w-4 h-4" /> Install App
      </a>
    );
  }

  if (!installPrompt || installed) return null;

  const handleInstall = async () => {
    installPrompt.prompt();
    await installPrompt.userChoice;
    setInstallPrompt(null);
  };

  return (
    <button
      onClick={handleInstall}
      className="md:hidden fixed bottom-5 left-5 z-[55] flex items-center gap-2 bg-[#b5622f] text-white pl-4 pr-5 py-3.5 rounded-full shadow-2xl text-[12px] uppercase tracking-[0.12em]"
    >
      <Download className="w-4 h-4" /> Install App
    </button>
  );
}

/* Mobile-only persistent "Inquiry" button — the quotation-basket shortcut. */
function StickyInquiryButton() {
  const { count, openCart } = useInquiryCart();
  return (
    <button
      onClick={openCart}
      className="md:hidden fixed bottom-5 right-5 z-[55] flex items-center gap-2 bg-[#241811] text-[#f4ece0] pl-4 pr-5 py-3.5 rounded-full shadow-2xl text-[12px] uppercase tracking-[0.12em]"
      aria-label="Open inquiry cart"
    >
      <ClipboardList className="w-4 h-4" />
      Inquiry{count > 0 ? ` · ${count}` : ''}
    </button>
  );
}

function ScrollManager() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      // let the target render, then scroll to it
      const id = hash.replace('#', '');
      requestAnimationFrame(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
      });
    } else {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
  }, [pathname, hash]);
  return null;
}

function Shell() {
  const [searchOpen, setSearchOpen] = useState(false);
  return (
    <div className="text-[#221a14] bg-[#f4ece0] min-h-screen" style={{ fontFamily: "'Inter', sans-serif" }}>
      <GlobalStyles />
      <ScrollManager />
      <PublicHeader onOpenSearch={() => setSearchOpen(true)} />
      <main>
        <Outlet context={{ openSearch: () => setSearchOpen(true) }} />
      </main>
      <PublicFooter />
      <InquiryCartDrawer />
      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
      <StickyInquiryButton />
      {/* <PublicAssistant /> */}
      <InstallAppButton />
    </div>
  );
}

export default function PublicLayout() {
  return (
    <PublicDataProvider>
      <WishlistProvider>
        <InquiryCartProvider>
          <Shell />
        </InquiryCartProvider>
      </WishlistProvider>
    </PublicDataProvider>
  );
}
